import { Card, Badge, Button, Table, Pagination, Form, Row, Col } from 'react-bootstrap';
import { Fragment, useState, useMemo } from 'react';
import { FaEye, FaEdit } from 'react-icons/fa';
import {
  RelatorioAtividadeDto,
  isReenvioDiretoCorregedoriaPendente,
} from '@/modules/reports/application/relatorios-atividades.service';
import { RELATORIO_CORES } from '../constants/relatorio-cores';
import { getPrazoEnvioInfo, getPrazoFinalEnvioUTC } from '../utils/prazo-envio-utils';
import RelatorioHistoricoModal from '@/modules/reports/presentation/components/RelatorioHistoricoModal';

interface RelatoriosAtividadesListProps {
  relatorios?: RelatorioAtividadeDto[];
  loading?: boolean;
  onEdit?: (relatorio: RelatorioAtividadeDto) => void;
  onView?: (relatorio: RelatorioAtividadeDto) => void;
  /** Quando true, exibe o botão de histórico de movimentação. Pensado para uso pela Corregedoria. */
  mostrarBotaoHistorico?: boolean;
  /** Consulta somente leitura (ex.: perfis administrativos vendo relatórios de um defensor). */
  mostrarBotaoEditar?: boolean;
}

export default function RelatoriosAtividadesList({
  relatorios = [],
  loading = false,
  onEdit,
  onView,
  mostrarBotaoHistorico = true,
  mostrarBotaoEditar = true,
}: RelatoriosAtividadesListProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [tipoFiltro, setTipoFiltro] = useState<string>('');
  const [statusFiltro, setStatusFiltro] = useState<string>('');
  const [historicoRelatorio, setHistoricoRelatorio] = useState<RelatorioAtividadeDto | null>(null);
  const itemsPerPage = 10;

  // Filtra os relatórios baseado em tipo e status
  const relatoriosFiltrados = useMemo(() => {
    return relatorios.filter((relatorio) => {
      const matchTipo = !tipoFiltro || relatorio.tipo === tipoFiltro;
      let matchStatus = true;

      if (statusFiltro === 'nao_enviados') {
        // Não enviados: tudo que ainda não foi efetivamente enviado
        matchStatus =
          relatorio.status !== 'enviado' &&
          relatorio.status !== 'aprovado' &&
          relatorio.status !== 'reprovado';
      } else if (statusFiltro === 'enviados') {
        // Enviados: já enviados (aguardando, aprovado ou reprovado)
        matchStatus =
          relatorio.status === 'enviado' ||
          relatorio.status === 'aprovado' ||
          relatorio.status === 'reprovado';
      } else if (statusFiltro) {
        matchStatus = relatorio.status === statusFiltro;
      }

      return matchTipo && matchStatus;
    });
  }, [relatorios, tipoFiltro, statusFiltro]);

  const totalPages = Math.ceil(relatoriosFiltrados.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = relatoriosFiltrados.slice(startIndex, endIndex);

  const getStatusBadge = (relatorio: RelatorioAtividadeDto) => {
    switch (relatorio.status) {
      case 'rascunho':
        return <Badge bg="warning" text="dark">Rascunho (não enviado)</Badge>;
      case 'enviado':
        return <Badge bg="info">Aguardando análise da Diretoria</Badge>;
      case 'aprovado':
        return relatorio.corregedoriaAprovadoEm ? (
          <Badge bg="success">Aprovado pela Corregedoria (final)</Badge>
        ) : (
          <Badge bg="primary">Aprovado pela Diretoria (aguardando Corregedoria)</Badge>
        );
      case 'reprovado':
        if (isReenvioDiretoCorregedoriaPendente(relatorio)) {
          return (
            <Badge bg="warning" text="dark">
              Corregedoria: aguardando reenvio direto
            </Badge>
          );
        }
        return relatorio.dataAprovacao ? (
          <Badge bg="danger">Reprovado pela Corregedoria</Badge>
        ) : (
          <Badge bg="danger">Reprovado pela Diretoria</Badge>
        );
      case 'pendente':
        return <Badge bg="secondary">Pendente de processamento</Badge>;
      default:
        return <Badge bg="secondary">Desconhecido</Badge>;
    }
  };

  const getPrazoForaBadge = (relatorio: RelatorioAtividadeDto) => {
    const info = getPrazoEnvioInfo(relatorio);
    if (!info?.isForaPrazo) return null;

    const tooltip = `Prazo final: ${info.prazoFinalLabel}${info.enviadoEmLabel ? ` | Enviado em: ${info.enviadoEmLabel}` : ''}`;
    return (
      <Badge bg="warning" text="dark" title={tooltip}>
        Fora do prazo
      </Badge>
    );
  };

  const getPrazoRestanteBadge = (relatorio: RelatorioAtividadeDto) => {
    const prazoFinalUtcMs = getPrazoFinalEnvioUTC(relatorio.mes, relatorio.ano);
    if (!prazoFinalUtcMs) return null;

    const hoje = new Date();
    const hojeUtcMs = Date.UTC(hoje.getFullYear(), hoje.getMonth(), hoje.getDate(), 0, 0, 0, 0);
    const diasRestantes = Math.round((prazoFinalUtcMs - hojeUtcMs) / (24 * 60 * 60 * 1000));

    let bg: 'success' | 'warning' | 'danger' = 'success';
    if (diasRestantes <= 1) {
      bg = 'danger';
    } else if (diasRestantes >= 2 && diasRestantes <= 4) {
      bg = 'warning';
    }

    if (diasRestantes < 0) {
      return <Badge bg="danger">Fora do prazo</Badge>;
    }

    return (
      <Badge bg={bg} text={bg === 'warning' ? 'dark' : undefined}>
        {diasRestantes} dia(s) para o prazo
      </Badge>
    );
  };

  const getTipoBadge = (tipo: string) => {
    switch (tipo) {
      case 'civil':
        return <Badge style={{ backgroundColor: RELATORIO_CORES.PRIMARIA }}>Cível</Badge>;
      case 'criminal':
        return <Badge bg="danger">Criminal</Badge>;
      default:
        return <Badge bg="secondary">Desconhecido</Badge>;
    }
  };

  const formatarData = (data: string | Date | undefined) => {
    if (!data) return 'Data não disponível';
    
    try {
      let date: Date;
      
      // Se já é um objeto Date
      if (data instanceof Date) {
        date = data;
      } else if (typeof data === 'string') {
        // Tentar diferentes formatos de data
        // Formato ISO: "2026-01-27T21:00:23.000Z"
        // Formato com timezone: "2026-01-27T21:00:23.000+00:00"
        // Formato simples: "2026-01-27"
        date = new Date(data);
      } else {
        return 'Formato inválido';
      }
      
      // Verificar se a data é válida
      if (isNaN(date.getTime())) {
        console.warn('Data inválida recebida:', data);
        return 'Data inválida';
      }
      
      // Formatar para pt-BR
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch (error) {
      console.error('Erro ao formatar data:', error, data);
      return 'Data inválida';
    }
  };

  const cardStyle = {
    border: `1px solid ${RELATORIO_CORES.BORDA_CARD}`,
    borderRadius: '8px',
    boxShadow: RELATORIO_CORES.SOMBRA_CARD,
  };
  const headerStyle = {
    backgroundColor: RELATORIO_CORES.CINZA_CLARO,
    color: RELATORIO_CORES.PRIMARIA,
    borderBottom: `1px solid ${RELATORIO_CORES.BORDA_CARD}`,
    borderRadius: '8px 8px 0 0',
  };

  if (loading) {
    return (
      <Card className="shadow-sm" style={cardStyle}>
        <Card.Header style={headerStyle}>
          <h5 className="mb-0 fw-bold" style={{ color: RELATORIO_CORES.PRIMARIA }}>Histórico de Relatórios</h5>
        </Card.Header>
        <Card.Body className="text-center text-muted" style={{ backgroundColor: RELATORIO_CORES.BRANCO }}>
          Carregando relatórios...
        </Card.Body>
      </Card>
    );
  }

  const renderHeader = () => (
    <Card.Header style={headerStyle}>
      <Row className="align-items-center">
        <Col md={6}>
          <h5 className="mb-0 fw-bold" style={{ color: RELATORIO_CORES.PRIMARIA }}>Histórico de Relatórios</h5>
        </Col>
        <Col md={6}>
          <Row className="g-2 justify-content-end">
            <Col md={5}>
              <Form.Select
                size="sm"
                value={tipoFiltro}
                onChange={(e) => {
                  setTipoFiltro(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ borderColor: RELATORIO_CORES.BORDA_CARD }}
              >
                <option value="">Todos os tipos</option>
                <option value="civil">Cível</option>
                <option value="criminal">Criminal</option>
              </Form.Select>
            </Col>
            <Col md={5}>
              <Form.Select
                size="sm"
                value={statusFiltro}
                onChange={(e) => {
                  setStatusFiltro(e.target.value);
                  setCurrentPage(1);
                }}
                style={{ borderColor: RELATORIO_CORES.BORDA_CARD }}
              >
                <option value="">Todos os status</option>
                <option value="nao_enviados">Não enviados</option>
                <option value="enviados">Enviados</option>
              </Form.Select>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card.Header>
  );

  return (
    <Fragment>
      <Card style={cardStyle}>
        {renderHeader()}
        <Card.Body className="p-0" style={{ backgroundColor: RELATORIO_CORES.BRANCO }}>
          <Table responsive hover striped className="mb-0" style={{ borderColor: RELATORIO_CORES.BORDA_CARD }}>
            <thead style={{ backgroundColor: RELATORIO_CORES.CINZA_CLARO, borderBottom: `1px solid ${RELATORIO_CORES.BORDA_CARD}` }}>
              <tr>
                <th style={{ color: RELATORIO_CORES.CINZA_ESCURO, fontWeight: 600 }}>Período</th>
                <th style={{ color: RELATORIO_CORES.CINZA_ESCURO, fontWeight: 600 }}>Defensoria</th>
                <th style={{ color: RELATORIO_CORES.CINZA_ESCURO, fontWeight: 600 }}>Tipo</th>
                <th style={{ color: RELATORIO_CORES.CINZA_ESCURO, fontWeight: 600 }}>Status</th>
                <th style={{ color: RELATORIO_CORES.CINZA_ESCURO, fontWeight: 600 }}>Data de envio</th>
                <th style={{ color: RELATORIO_CORES.CINZA_ESCURO, fontWeight: 600 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {relatoriosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-muted">
                    Não há relatórios!
                  </td>
                </tr>
              ) : (
                currentItems.map((relatorio) => {
                  // Calcular período se não vier do backend
                  const periodo = relatorio.periodo || (() => {
                    const meses = [
                      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
                    ];
                    const mesNumero = typeof relatorio.mes === 'string' 
                      ? parseInt(relatorio.mes) 
                      : relatorio.mes;
                    const nomeMes = meses[mesNumero - 1] || String(mesNumero).padStart(2, '0');
                    return `${nomeMes}/${relatorio.ano}`;
                  })();

                  return (
                    <tr key={relatorio.id}>
                      <td className="fw-bold">
                        <div className="d-flex flex-column align-items-start gap-1">
                          <span>{periodo}</span>
                          {getPrazoRestanteBadge(relatorio)}
                        </div>
                      </td>
                      <td>{relatorio.defensoriaName || 'Não informado'}</td>
                      <td>{getTipoBadge(relatorio.tipo)}</td>
                      <td>
                        <div>
                          <div className="d-flex flex-wrap gap-2 align-items-center">
                            {getStatusBadge(relatorio)}
                            {getPrazoForaBadge(relatorio)}
                          </div>
                          {relatorio.status === 'reprovado' && relatorio.justificativaReprovacao && (
                            <div className="mt-1">
                              <small className="text-danger d-block" style={{ fontSize: '0.75rem' }}>
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                {relatorio.justificativaReprovacao}
                              </small>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="text-muted small">
                        {relatorio.dataEnvio ? (
                          formatarData(relatorio.dataEnvio)
                        ) : (
                          <span className="fst-italic">-</span>
                        )}
                      </td>
                    <td>
                      <div className="d-flex align-items-center gap-3 flex-wrap">
                        <button
                          type="button"
                          className="btn btn-link p-0"
                          onClick={() => onView?.(relatorio)}
                          title="Visualizar relatório"
                        >
                          <FaEye size={20} color="#0daaaf" />
                        </button>

                        {mostrarBotaoEditar && (relatorio.status === 'rascunho' || relatorio.status === 'reprovado') && (
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => onEdit?.(relatorio)}
                            title={relatorio.status === 'reprovado' ? 'Corrigir relatório' : 'Editar relatório'}
                          >
                            <FaEdit size={20} color={RELATORIO_CORES.PRIMARIA} />
                          </button>
                        )}

                        {mostrarBotaoHistorico && (
                          <Button
                            size="sm"
                            variant="outline-primary"
                            style={{ backgroundColor: 'transparent', borderColor: RELATORIO_CORES.PRIMARIA, color: RELATORIO_CORES.PRIMARIA }}
                            onClick={() => setHistoricoRelatorio(relatorio)}
                            title="Ver histórico de movimentação"
                          >
                            <i className="bi bi-clock-history me-1"></i>
                            Histórico
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {relatoriosFiltrados.length > 0 && totalPages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            />
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <Pagination.Item
                key={page}
                active={page === currentPage}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      <RelatorioHistoricoModal
        show={historicoRelatorio !== null}
        relatorio={historicoRelatorio}
        onHide={() => setHistoricoRelatorio(null)}
      />
    </Fragment>
  );
}
